import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { subscribeTrash, restoreBD, permanentlyDeleteBD, purgeOldTrash, TRASH_RETENTION_DAYS } from '../utils/firestoreService';

const T = {
  orange:'var(--orange)', ink:'var(--ink)', bg:'var(--bg)', surface:'var(--surface)',
  border:'var(--border)', muted:'var(--muted)', subtle:'var(--subtle)', red:'#DC2626', green:'#16A34A',
};
const FONT    = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const DISPLAY = "'Bangers','Arial Black',cursive";

const daysAgo = (ts) => {
  if (!ts?.toDate) return null;
  const days = Math.floor((Date.now() - ts.toDate().getTime()) / 86400000);
  return days;
};

export default function TrashModal({ onClose, onToast }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null); // null = chargement
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const unsub = subscribeTrash(user.uid, (data) => {
      const sorted = data.sort((a,b)=>(b.deletedAt?.toMillis?.()||0)-(a.deletedAt?.toMillis?.()||0));
      setItems(sorted);
      purgeOldTrash(user.uid, sorted).catch(()=>{}); // silencieux ; le listener se met à jour tout seul
    }, () => onToast?.('Erreur de chargement de la corbeille', 'error'));
    return unsub;
  }, [user.uid, onToast]);

  const handleRestore = async (item) => {
    setBusyId(item.id);
    try {
      await restoreBD(user.uid, item.id);
      onToast?.(`"${item.titre || item.serie || 'Album'}" restauré ✓`);
    } catch(e) { onToast?.('Erreur : '+e.message, 'error'); }
    setBusyId(null);
  };

  const handlePurge = async (item) => {
    if (!window.confirm('Supprimer définitivement cet album ? Cette action est irréversible.')) return;
    setBusyId(item.id);
    try { await permanentlyDeleteBD(user.uid, item.id); }
    catch(e) { onToast?.('Erreur : '+e.message, 'error'); }
    setBusyId(null);
  };

  const handlePurgeAll = async () => {
    if (!items?.length || !window.confirm(`Vider la corbeille ? ${items.length} album${items.length>1?'s seront':' sera'} supprimé${items.length>1?'s':''} définitivement.`)) return;
    setBusyId('all');
    try { await Promise.all(items.map(it => permanentlyDeleteBD(user.uid, it.id))); }
    catch(e) { onToast?.('Erreur : '+e.message, 'error'); }
    setBusyId(null);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1000,backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:T.bg,borderRadius:'22px 22px 0 0',border:`1px solid ${T.border}`,borderBottom:'none',width:'100%',maxWidth:'560px',maxHeight:'92vh',overflowY:'auto',overflowX:'hidden',fontFamily:FONT,boxShadow:'0 -12px 40px rgba(0,0,0,.18)'}}>
        <div style={{position:'sticky',top:0,zIndex:10,background:'var(--header-bg)',backdropFilter:'blur(10px)',borderBottom:`1px solid ${T.border}`,padding:'1rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:DISPLAY,fontSize:'1.35rem',letterSpacing:'.05em',color:T.orange}}>🗑️ Corbeille</div>
            <div style={{fontSize:'12px',color:T.muted,marginTop:'1px'}}>Récupérable pendant {TRASH_RETENTION_DAYS} jours</div>
          </div>
          <button onClick={onClose} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.surface,cursor:'pointer',fontSize:'16px',color:T.muted}}>×</button>
        </div>

        <div style={{padding:'1.25rem'}}>
          {items === null && (
            <div style={{textAlign:'center',padding:'2rem',color:T.muted,fontSize:'13px'}}>Chargement…</div>
          )}

          {items?.length === 0 && (
            <div style={{textAlign:'center',padding:'2.5rem 1rem'}}>
              <div style={{fontSize:'2.2rem',marginBottom:'8px'}}>🗑️</div>
              <div style={{fontSize:'14px',fontWeight:700,color:T.ink}}>Corbeille vide</div>
              <div style={{fontSize:'12px',color:T.muted,marginTop:'2px'}}>Les albums supprimés apparaîtront ici pendant {TRASH_RETENTION_DAYS} jours.</div>
            </div>
          )}

          {items && items.length > 0 && (
            <>
              <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'10px'}}>
                <button onClick={handlePurgeAll} disabled={busyId==='all'}
                  style={{border:'none',background:'transparent',color:T.red,fontFamily:FONT,fontSize:'12px',fontWeight:700,cursor:'pointer',opacity:busyId==='all'?.5:1}}>
                  Vider la corbeille
                </button>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {items.map(it => {
                  const d = daysAgo(it.deletedAt);
                  const busy = busyId === it.id;
                  return (
                    <div key={it.id} style={{display:'flex',alignItems:'center',gap:'10px',background:T.surface,border:`1px solid ${T.border}`,borderRadius:'12px',padding:'10px 12px',opacity:busy?.5:1}}>
                      {it.photos?.[0]?.url ? (
                        <img src={it.photos[0].url} alt="" style={{width:'40px',height:'40px',borderRadius:'8px',objectFit:'cover',flexShrink:0}}/>
                      ) : (
                        <div style={{width:'40px',height:'40px',borderRadius:'8px',background:T.bg,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px'}}>📕</div>
                      )}
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{fontSize:'13px',fontWeight:700,color:T.ink,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {it.serie ? `${it.serie}${it.numero?` T.${it.numero}`:''}` : (it.titre || '(Sans titre)')}
                        </div>
                        <div style={{fontSize:'11px',color:T.muted}}>
                          {d === null ? '' : d === 0 ? "Supprimé aujourd'hui" : `Supprimé il y a ${d} jour${d>1?'s':''}`}
                        </div>
                      </div>
                      <button onClick={()=>handleRestore(it)} disabled={busy}
                        style={{padding:'6px 11px',borderRadius:'8px',border:'none',background:T.green,color:'#fff',fontFamily:FONT,fontSize:'12px',fontWeight:700,cursor:'pointer',flexShrink:0}}>
                        ↺ Restaurer
                      </button>
                      <button onClick={()=>handlePurge(it)} disabled={busy} title="Supprimer définitivement"
                        style={{width:'30px',height:'30px',borderRadius:'8px',border:`1px solid ${T.border}`,background:'transparent',color:T.red,fontSize:'13px',cursor:'pointer',flexShrink:0}}>
                        🗑️
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
